import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useResolveSuspectDay, useSuspectDayDetail } from '../../api/revenue';
import type { SuspectDayDetailDay, SuspectResolution } from '../../api/revenue';
import { useMachineLatestMeters, useMachineMeterQuarantine, useMyPermissions } from '../../api/queries';
import { useAlert, useConfirm } from '../../components/dialogs/DialogProvider';
import { MeterQuarantineDialog } from '../machines/MeterQuarantineDialog';
import { fmtDate, fmtDateTime, fmtOrEmDash } from '../../utils/format';

/**
 * The header fields this dialog needs about the day being inspected. The
 * revenue flow passes a full SuspectDay (structurally compatible); the
 * machines-area Daily meters tab passes a minimal object built from its own
 * row (any gaming day can be inspected there, suspect or not — `suspect:
 * false` hides the review banner/actions).
 */
export type SuspectDayHeader = {
  id: string;
  machineLabel: string;
  siteName?: string | null;
  gameName?: string | null;
  gamingDay?: string;
  /** Defaults to true (the revenue flow only lists suspect days). */
  suspect?: boolean;
  suspectReason?: string | null;
  suspectReviewedAt?: string | null;
  suspectReviewedByName?: string | null;
};

/**
 * "Meter-day detail" dialog. Opened from a row in SuspectDaysDialog or the
 * machines-area Daily meters tab. Shows the REAL 33 cumulative machine meters
 * for the PREVIOUS day, THIS day and the NEXT day side-by-side so a reviewer
 * can see exactly what moved (or didn't) around the flagged day.
 *
 * The engine's suspect reason + the review state are shown prominently at the
 * top (suspect days only). When a meter's value equals its PREVIOUS-day value
 * the cell is marked "frozen" — the tell of a quarantine freeze, where the
 * engine held the day at last-known-good after rejecting a bad reading.
 *
 * `allowResolve` renders the Confirm good / Confirm suspect actions INSIDE the
 * dialog (owner request: reviews must be doable from the multi-meter display,
 * not only from a column that can sit off-screen). Gated on meters.audit-ack.
 *
 * Meter values are raw credit COUNTS (integers), NOT cents — rendered as plain
 * integers, never currency.
 */
export function SuspectDayDetailDialog({
  open, onClose, day, allowResolve = false,
}: {
  open: boolean;
  onClose: () => void;
  day: SuspectDayHeader | null;
  allowResolve?: boolean;
}) {
  const q = useSuspectDayDetail(open ? (day?.id ?? null) : null);
  const detail = q.data;
  const isSuspect = day?.suspect !== false;

  // Owner review 2026-08-22: the frozen day columns alone are useless evidence
  // (this day is a grey copy of the previous day). When the machine's meter
  // engine is QUARANTINED, pull the raw packets around the event so the
  // reviewer sees what the machine ACTUALLY reported (e.g. all zeros + a $10
  // bill) next to the last accepted values — and can resolve from here.
  const quarantine = useMachineMeterQuarantine(open ? (detail?.machineId ?? null) : null);
  // "Next" fallback: today's suspect day has no next day yet — show the
  // machine's CURRENT (live) meters in that column instead of a blank.
  const latest = useMachineLatestMeters(open && detail && detail.nextDay == null ? detail.machineId : null);
  const [resolveOpen, setResolveOpen] = useState(false);

  const perms = useMyPermissions();
  const canAck = !!perms.data?.codes.includes('meters.audit-ack');
  const resolve = useResolveSuspectDay();
  const confirm = useConfirm();
  const alert = useAlert();

  async function onResolve(resolution: SuspectResolution) {
    if (!day) return;
    const ok = await confirm(
      resolution === 'good'
        ? {
            title: 'Confirm meters good',
            message: `Confirm ${day.machineLabel}${day.gamingDay ? ` on ${fmtDate(day.gamingDay)}` : ''} is good? This clears the suspect flag.`,
            confirmLabel: 'Confirm good',
          }
        : {
            title: 'Keep flagged as suspect',
            message: `Keep ${day.machineLabel}${day.gamingDay ? ` on ${fmtDate(day.gamingDay)}` : ''} flagged as suspect? It stays flagged but is marked reviewed.`,
            confirmLabel: 'Confirm suspect',
          },
    );
    if (!ok) return;
    try {
      await resolve.mutateAsync({ id: day.id, resolution });
      onClose();
    } catch (e) {
      await alert({
        title: 'Could not resolve',
        message: (e as { message?: string })?.message ?? 'Failed to resolve the suspect meter-day.',
        severity: 'error',
      });
    }
  }

  const showResolve = allowResolve && canAck && !!day && isSuspect && !day.suspectReviewedAt;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Meter detail{day ? ` — ${fmtOrEmDash(day.machineLabel)}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {day && isSuspect && (
          <Stack spacing={1} sx={{ mb: 2 }}>
            <ReasonBanner day={day} />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <ReviewStateChip day={day} />
              <Typography variant="body2" color="text.secondary">
                {fmtOrEmDash(day.siteName)}
                {day.gameName ? ` · ${day.gameName}` : ''}
              </Typography>
            </Stack>
          </Stack>
        )}

        {q.isLoading && (
          <Typography variant="body2" color="text.secondary">Loading meter detail…</Typography>
        )}
        {q.isError && (
          <Alert severity="error">
            {(q.error as { message?: string })?.message ?? 'Failed to load meter detail.'}
          </Alert>
        )}

        {detail && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Cumulative meter closes (credit counts). A value marked <em>frozen</em> means the engine REJECTED
              what the machine reported and held the day at the last good value — the numbers below are NOT what
              the machine sent. {quarantine.data?.quarantined
                ? 'See "What the machine actually reported" underneath for the rejected reading.'
                : ''}
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                size="small"
                aria-label="suspect meter detail"
                sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Meter</TableCell>
                    <TableCell align="right">Previous ({dayHeader(detail.previousDay)})</TableCell>
                    <TableCell align="right">This day ({dayHeader(detail.suspectDay)})</TableCell>
                    <TableCell align="right">
                      {detail.nextDay ? `Next (${dayHeader(detail.nextDay)})` : 'Current (live)'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {Object.keys(detail.suspectDay.meters).map((key) => {
                    const prev = detail.previousDay?.meters[key];
                    const cur = detail.suspectDay.meters[key];
                    const next = detail.nextDay
                      ? detail.nextDay.meters[key]
                      : latest.data?.meters?.[snakeToCamel(key)];
                    const frozen = prev != null && cur === prev;
                    return (
                      <TableRow key={key} hover>
                        <TableCell>{meterLabel(key)}</TableCell>
                        <TableCell align="right">{fmtCount(prev)}</TableCell>
                        <TableCell
                          align="right"
                          sx={frozen ? { color: 'text.disabled', fontStyle: 'italic' } : undefined}
                        >
                          {fmtCount(cur)}
                          {frozen && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label="frozen"
                              sx={{ ml: 1, height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">{fmtCount(next)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {quarantine.data?.quarantined && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>What the machine actually reported.</strong> The engine has been rejecting this
                    machine&apos;s readings since {fmtDateTime(quarantine.data.since ?? null)} — money movement is
                    NOT being counted until this is resolved. Compare the last accepted reading with what the
                    machine sent (differences in red):
                  </Typography>
                </Alert>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" aria-label="rejected machine reading"
                    sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Meter</TableCell>
                        <TableCell align="right">
                          Last accepted
                          <Typography variant="caption" color="text.secondary" display="block">
                            {quarantine.data.lastAccepted?.receivedAt ? fmtDateTime(quarantine.data.lastAccepted.receivedAt) : fmtOrEmDash(null)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          Machine reported (rejected)
                          <Typography variant="caption" color="text.secondary" display="block">
                            {quarantine.data.firstAfter?.receivedAt ? fmtDateTime(quarantine.data.firstAfter.receivedAt) : fmtOrEmDash(null)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          Latest reported
                          <Typography variant="caption" color="text.secondary" display="block">
                            {quarantine.data.latest?.receivedAt ? fmtDateTime(quarantine.data.latest.receivedAt) : fmtOrEmDash(null)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {Object.keys(quarantine.data.firstAfter?.meters ?? quarantine.data.lastAccepted?.meters ?? {}).map((key) => {
                        const before = quarantine.data!.lastAccepted?.meters[key];
                        const reported = quarantine.data!.firstAfter?.meters[key];
                        const different = before != null && reported != null && reported !== before;
                        return (
                          <TableRow key={key} hover>
                            <TableCell>{meterLabel(key)}</TableCell>
                            <TableCell align="right">{fmtCount(before)}</TableCell>
                            <TableCell align="right"
                              sx={different ? { color: 'error.main', fontWeight: 600 } : undefined}>
                              {fmtCount(reported)}
                            </TableCell>
                            <TableCell align="right">{fmtCount(quarantine.data!.latest?.meters[key])}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Button color="warning" variant="contained" onClick={() => setResolveOpen(true)}>
                    Review &amp; resolve quarantine
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {showResolve && (
          <>
            <Button color="success" variant="outlined" disabled={resolve.isPending}
              onClick={() => void onResolve('good')}>
              Confirm good
            </Button>
            <Button color="warning" variant="outlined" disabled={resolve.isPending}
              onClick={() => void onResolve('suspect')}>
              Confirm suspect
            </Button>
          </>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {resolveOpen && detail && (
        <MeterQuarantineDialog
          open
          onClose={() => setResolveOpen(false)}
          machineId={detail.machineId}
          machineLabel={detail.machineLabel}
        />
      )}
    </Dialog>
  );
}

function ReasonBanner({ day }: { day: SuspectDayHeader }) {
  const explanation = day.suspectReason ? reasonExplanation(day.suspectReason) : null;
  return (
    <Alert severity="warning" icon={false} sx={{ py: 0.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="body2" component="span"><strong>Flagged suspect:</strong></Typography>
        {day.suspectReason ? (
          <Chip size="small" color="warning" label={reasonLabel(day.suspectReason)} />
        ) : (
          <Typography variant="body2" component="span" color="text.secondary">reason not recorded</Typography>
        )}
      </Stack>
      {explanation && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>{explanation}</Typography>
      )}
    </Alert>
  );
}

function ReviewStateChip({ day }: { day: SuspectDayHeader }) {
  if (!day.suspectReviewedAt) {
    return <Chip size="small" color="warning" label="Needs review" />;
  }
  const who = day.suspectReviewedByName;
  return <Chip size="small" color="default" label={who ? `Reviewed by ${who}` : 'Reviewed'} />;
}

/** Column header date for a day (or an em-dash when the neighbour is absent). */
function dayHeader(d: SuspectDayDetailDay | null | undefined): string {
  return d ? fmtDate(d.gamingDay) : fmtOrEmDash(null);
}

/** Raw credit count — plain integer (thousands-separated), em-dash when absent.
 *  NOT currency: these are meter counts, not cents. */
function fmtCount(v: number | null | undefined): string {
  return v == null ? fmtOrEmDash(null) : v.toLocaleString();
}

// Human-readable labels for the 33 schema-1 meter columns. Any key not listed
// (a future meter) falls back to a title-cased form of the column name.
const METER_LABELS: Record<string, string> = {
  total_coin_in_credits: 'Coin in',
  total_coin_out_credits: 'Coin out',
  total_jackpot_credits: 'Jackpot',
  total_cancelled_credits: 'Cancelled credits',
  games_played: 'Games played',
  total_credits_from_bills_accepted: 'Bills accepted (credits)',
  current_credits: 'Current credits',
  sas_cashable_ticket_in_cents: 'Cashable ticket in',
  sas_cashable_ticket_out_cents: 'Cashable ticket out',
  sas_restricted_ticket_in_cents: 'Restricted ticket in',
  sas_restricted_ticket_out_cents: 'Restricted ticket out',
  electronic_transfers_to_machine: 'AFT to machine',
  electronic_transfers_to_host: 'AFT to host',
  current_restricted_credits: 'Current restricted credits',
  total_hand_paid_credits: 'Hand paid',
  total_drop_credits: 'Drop',
  bills_1_dollar_accepted: '$1 bills',
  bills_2_dollar_accepted: '$2 bills',
  bills_5_dollar_accepted: '$5 bills',
  bills_10_dollar_accepted: '$10 bills',
  bills_20_dollar_accepted: '$20 bills',
  bills_50_dollar_accepted: '$50 bills',
  bills_100_dollar_accepted: '$100 bills',
  bills_200_dollar_accepted: '$200 bills',
  bills_500_dollar_accepted: '$500 bills',
  bills_1000_dollar_accepted: '$1,000 bills',
  bills_2000_dollar_accepted: '$2,000 bills',
  bills_5000_dollar_accepted: '$5,000 bills',
  bills_10000_dollar_accepted: '$10,000 bills',
  bills_20000_dollar_accepted: '$20,000 bills',
  bills_50000_dollar_accepted: '$50,000 bills',
  bills_100000_dollar_accepted: '$100,000 bills',
  total_credits_from_bills_to_drop: 'Bills to drop (credits)',
};

export function meterLabel(key: string): string {
  return METER_LABELS[key] ?? titleCase(key);
}

/**
 * Plain-English names + explanations for the engine's suspect/quarantine
 * reason tokens (owner review 2026-08-22: "overflow implausible shrink" means
 * nothing to a reviewer). Unknown tokens fall back to a title-cased form.
 */
const REASONS: Record<string, { label: string; explain: string }> = {
  overflow_implausible_shrink: {
    label: 'Meters went backwards',
    explain: 'The machine reported cumulative meters LOWER than previously recorded — usually a RAM clear/reset or a machine fault. The meters engine stopped counting, froze this day at the last good values, and keeps recording what the machine sends until the event is reviewed and resolved.',
  },
  ram_reset_shrink: {
    label: 'RAM reset detected',
    explain: 'The machine’s meters restarted from near zero with money already on them. The engine confirmed a RAM reset and started a new meter baseline at zero — review to confirm the reset was legitimate.',
  },
  ram_reset_confirmed: {
    label: 'RAM reset confirmed',
    explain: 'The machine’s meters were cleared and the engine started a new meter baseline at zero. Revenue counts from the reset onward.',
  },
  reset_candidate_all_zero: {
    label: 'Meters reset to zero',
    explain: 'Every meter on the machine read zero after previously carrying values — a clean RAM clear.',
  },
  reset_candidate_shrink: {
    label: 'Meters dropped to a fraction of their previous values',
    explain: 'The core meters fell below half of their previous values — treated as a RAM-reset candidate pending confirmation over the following readings.',
  },
  reset_candidate_ambiguous: {
    label: 'Possible reset — could not confirm',
    explain: 'The readings after a suspected reset neither confirmed the reset nor matched the previous values. The engine stopped counting until a person reviews it.',
  },
  quarantine: {
    label: 'Reading rejected',
    explain: 'The engine rejected a reading from this machine and held the day at the last good values pending review.',
  },
  gap_implausible: {
    label: 'Implausible jump after an offline gap',
    explain: 'The meter movement across an offline gap exceeded plausibility limits. The movement IS counted, but the day is flagged for review.',
  },
};

/** Plain-English label for an engine reason token. */
export function reasonLabel(reason: string): string {
  return REASONS[reason]?.label ?? titleCase(reason);
}

/** One-sentence explanation of what the reason means, or null for unknown tokens. */
export function reasonExplanation(reason: string): string | null {
  return REASONS[reason]?.explain ?? null;
}

/** snake_case → camelCase (the latest-meters endpoint keys its map camelCase
 *  while the day-detail meters use the snake_case column names). */
function snakeToCamel(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());
}

function titleCase(key: string): string {
  return key
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
